import {
    Modal,
    ModalOverlay,
    ModalContent,
    Table,
    TableContainer,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Link,
    Spinner,
    Center,
    Button,
    Text,
    Box,
    HStack,
    VStack,
    Icon,
    IconButton,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    useToast,
    Flex,
    Image,
    Switch,
    CloseButton,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverArrow,
    PopoverBody,
    Portal,
    Input,
    InputGroup,
    InputLeftElement,
} from '@chakra-ui/react';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { TbInfoCircle, TbFlag, TbCopy, TbArrowRight, TbPencil, TbMessage, TbArrowUp, TbArrowDown, TbSearch } from 'react-icons/tb';
import { useGetPartOptions, PartOption, NoQuote, TabOptions } from '../hooks/useGetPartOptions';
import { useUpdatePartOption } from '../hooks/useUpdatePartOption';
import PartOptionNoteForm from '../components/PartOptionNoteForm';
import { formatPrice } from '../functions';
import VendorTypeIcon from '../components/VendorTypeIcon';
import PartType from '../components/PartType';
import { PartType as PartTypeEnum } from '../hooks/useGetSettings';
import { ReactComponent as MotorIcon } from '../assets/Motor.svg';
import YaxleLogo from '../assets/yaxleLogoBlue.png';
import { brandCopy } from '../constants/brand';
import SortArrowIcon, { SortIconDirection } from '../components/SortArrowIcon';
import QuotingTag from '../components/QuotingTag';
import OrderTag from '../components/OrderTag';
import SourceBadge from '../components/SourceBadge';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import { useStickyTableCopyFront } from '../hooks/useStickyTableCopyFront';
import CopyTooltip from '../components/CopyTooltip';
import PartOptionAdvancedFilters from '../components/PartOptionAdvancedFilters';
import {
    applyPartOptionFilters,
    getAvailablePartTypesFromOptions,
    getPartOptionInvalidReason,
    getPartOptionTypeCategory,
    isPartOptionValid,
    PartOptionFilters,
} from '../utils/partOptionFilters';
import { requiresNonMceLkqNote } from '../utils/partOptionNotes';
import { COPY_ANCHOR_ATTR, copyOverflowTableSx, FILTER_MENU_Z_INDEX, stickyScrollTableHeadSx, stickyTableScrollCopySx, STICKY_TABLE_HEADER_Z_INDEX } from '../utils/tableCopySx';
import { ConfirmDialog, SegmentedControl, Tooltip } from '../ui';

interface PartOptionsModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderPartId: string;
    orderId: string;
    source: 'flagged' | 'order';
    drpJob?: boolean;
}

const noQuoteCopyTableSx = {
    [`& td:has([${COPY_ANCHOR_ATTR}])`]: {
        overflow: 'visible',
    },
};

const SOURCE_SEARCH_LABELS: Record<string, string> = {
    ccc: 'C',
    'ccc-sp': 'SP',
    tesla: 'T',
    opstrax: 'O',
    mitchell: 'M',
    partstrader: 'PT',
    collisionlink: 'CL',
    inventory: brandCopy('PartBay'),
};

const getPartTypeCategory = (type: PartTypeEnum | 'other'): string => {
    switch (type) {
        case 'oem': return 'OEM';
        case 'oe-surplus': return 'OE Surplus';
        case 'a/m': return 'A/M';
        case 'rcy': return 'Recycled';
        case 're-man': return 'Reman';
        case 're-con': return 'Recon';
        default: return 'Other';
    }
};

const optionMatchesSearch = (option: PartOption, query: string): boolean => {
    const supplierNoteBody = (option.supplierNote ?? '').replace(/^Part #:\s*.+?(\n|$)/, '');
    const sourceLabel = SOURCE_SEARCH_LABELS[option.source] ?? option.source;

    return (
        (option.supplierText ?? '').toLowerCase().includes(query) ||
        (option.type ?? '').toLowerCase().includes(query) ||
        (getPartTypeCategory(option.type as PartTypeEnum) ?? '').toLowerCase().includes(query) ||
        (getPartOptionTypeCategory(option.type) ?? '').toLowerCase().includes(query) ||
        (option.deliveryString ?? '').toLowerCase().includes(query) ||
        (option.supplierPartNumber ?? '').toLowerCase().includes(query) ||
        supplierNoteBody.toLowerCase().includes(query) ||
        (option.supplierComment?.toLowerCase().includes(query) ?? false) ||
        (option.optionNote?.toLowerCase().includes(query) ?? false) ||
        (option.source ?? '').toLowerCase().includes(query) ||
        (sourceLabel ?? '').toLowerCase().includes(query) ||
        String(option.qty).includes(query) ||
        formatPrice(option.listPrice).toLowerCase().includes(query) ||
        formatPrice(option.buyPrice).toLowerCase().includes(query) ||
        (option.adjustedListPrice != null && formatPrice(option.adjustedListPrice).toLowerCase().includes(query)) ||
        String(option.listPrice).includes(query) ||
        String(option.buyPrice).includes(query) ||
        (option.adjustedListPrice != null && String(option.adjustedListPrice).includes(query)) ||
        (option.vendorType?.toLowerCase().includes(query) ?? false) ||
        (getPartOptionInvalidReason(option)?.toLowerCase().includes(query) ?? false)
    );
};

const noQuoteMatchesSearch = (noQuote: NoQuote, query: string): boolean => (
    (noQuote.supplierText ?? '').toLowerCase().includes(query) ||
    (noQuote.noQuoteNumber ?? '').toLowerCase().includes(query)
);

const PartOptionsModal = ({ isOpen, onClose, orderPartId, orderId, source, drpJob = false }: PartOptionsModalProps) => {
    const { data, isLoading, error, refetch } = useGetPartOptions(orderPartId, isOpen);
    const { mutateAsync: updatePartOption, isPending: isUpdating } = useUpdatePartOption();
    const initiallySelected = useMemo(() => data?.options?.find(option => option.selected)?._id ?? null, [data]);
    const [selectedOption, setSelectedOption] = useState<PartOption | null>(null);
    const [isCorrectedPartConfirmOpen, setIsCorrectedPartConfirmOpen] = useState(false);
    const [isInvalidOptionConfirmOpen, setIsInvalidOptionConfirmOpen] = useState(false);
    const cancelCorrectedPartRef = useRef<HTMLButtonElement>(null);
    const [useListPriceForMargin, setUseListPriceForMargin] = useState(false);
    const [sortState, setSortState] = useState<{ field: 'delivery' | 'listPrice' | 'buyPrice' | 'grossMargin' | null; direction: 'asc' | 'desc'; }>({ field: null, direction: 'asc' });
    const [viewMode, setViewMode] = useState<TabOptions>('options');
    const toast = useToast();
    const { copy: copyText, isCopied } = useCopyToClipboard();
    const [failedCopyKey, setFailedCopyKey] = useState<string | null>(null);
    const failedCopyTimerRef = useRef<number | null>(null);
    const noQuoteScrollRef = useRef<HTMLDivElement>(null);
    const optionsScrollRef = useRef<HTMLDivElement>(null);
    const { scrollContainerProps: noQuoteScrollProps } = useStickyTableCopyFront(
        noQuoteScrollRef,
        `nq-${data?.noQuotes?.length ?? 0}`,
    );
    const { scrollContainerProps: optionsScrollProps } = useStickyTableCopyFront(
        optionsScrollRef,
        `opt-${data?.options?.length ?? 0}-${viewMode}`,
    );
    const isEditable = !!data?.canChangeSelections || !!data?.canConfirmSelections;
    const isConfirmingSelection = initiallySelected === selectedOption?._id;
    const isSaveAllowed = isConfirmingSelection ? !!data?.canConfirmSelections : !!data?.canChangeSelections;

    const [notePanelOption, setNotePanelOption] = useState<PartOption | null>(null);
    const [previewListPrice, setPreviewListPrice] = useState<number | null>(null);
    const [partOptionFilters, setPartOptionFilters] = useState<PartOptionFilters>({});
    const [showPerUnit, setShowPerUnit] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const initializedSelectionKey = useRef<string | null>(null);

    const openNotePanel = (option: PartOption) => {
        setNotePanelOption(option);
    };

    const closeNotePanel = () => {
        setNotePanelOption(null);
        setPreviewListPrice(null);
    };

    const optionRequiresNote = useCallback((option: PartOption) => requiresNonMceLkqNote({
        partType: option.type,
        drpJob,
        source: option.source,
        insurerBest: option.insurerBest,
    }), [drpJob]);

    useEffect(() => () => {
        if (failedCopyTimerRef.current !== null) {
            window.clearTimeout(failedCopyTimerRef.current);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) {
            initializedSelectionKey.current = null;
            return;
        }
        if (!data) return;
        const initKey = `${orderPartId}:${isOpen}`;
        if (initializedSelectionKey.current === initKey) return;
        initializedSelectionKey.current = initKey;
        const currentlySelected = data.options.find(option => option.selected);
        setSelectedOption(currentlySelected ?? null);
        if (currentlySelected && optionRequiresNote(currentlySelected) && !currentlySelected.optionNote?.trim()) {
            setNotePanelOption(currentlySelected);
        }
        setViewMode(data.defaultTab);
        setPartOptionFilters({});
        setShowPerUnit(false);
        setSearchQuery('');
    }, [data, isOpen, orderPartId, optionRequiresNote]);

    const handleOptionSelect = (option: PartOption) => {
        if (!isEditable) return;
        setSelectedOption(option);
        if (optionRequiresNote(option) && !option.optionNote?.trim()) {
            openNotePanel(option);
        } else {
            closeNotePanel();
        }
    };

    const closeModal = () => {
        setViewMode('options');
        setSearchQuery('');
        setIsInvalidOptionConfirmOpen(false);
        closeNotePanel();
        if (!isUpdating) {
            onClose();
        }
    };

    const requiresInvalidOptionAcknowledgement = (option: PartOption) => {
        const isDrp = data?.drpJob ?? drpJob;
        const isChangingSelection = initiallySelected !== option._id;
        return isDrp && isChangingSelection && !isPartOptionValid(option);
    };

    const performSave = async () => {
        if (!selectedOption || !orderPartId || !data?.options || isUpdating) return;
        if (optionRequiresNote(selectedOption) && !selectedOption.optionNote?.trim()) return;

        updatePartOption({
            orderPartId,
            selectedOption,
            orderId,
            source
        }).then(() => {
            closeModal();
        }).catch((error: any) => {
            toast({
                title: "Error",
                description: error?.response?.data?.message || "Failed to update part option. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        });
    };

    const handleSaveChanges = async () => {
        if (!selectedOption || !orderPartId || !data?.options || isUpdating) return;
        if (!isSaveAllowed) return;

        const hasCorrectedPartNumber = !!data.correctedPartNumber;
        const wasOem = data.selectedPartType === 'oem';
        const isSwitchingToNonOem = selectedOption.type !== 'oem';
        const isChangingSelection = initiallySelected !== selectedOption._id;

        if (hasCorrectedPartNumber && wasOem && isSwitchingToNonOem && isChangingSelection) {
            setIsCorrectedPartConfirmOpen(true);
            return;
        }

        if (requiresInvalidOptionAcknowledgement(selectedOption)) {
            setIsInvalidOptionConfirmOpen(true);
            return;
        }

        await performSave();
    };

    const handleConfirmCorrectedPartSwitch = async () => {
        setIsCorrectedPartConfirmOpen(false);
        if (selectedOption && requiresInvalidOptionAcknowledgement(selectedOption)) {
            setIsInvalidOptionConfirmOpen(true);
            return;
        }
        await performSave();
    };

    const handleConfirmInvalidOption = async () => {
        await performSave();
        setIsInvalidOptionConfirmOpen(false);
    };

    const calculateGrossMargin = (listPrice: number, buyPrice: number): number => {
        if (listPrice === 0) return 0;
        return ((listPrice - buyPrice) / listPrice) * 100;
    };

    const toggleSort = (field: 'delivery' | 'listPrice' | 'buyPrice' | 'grossMargin') => {
        setSortState((prev) => {
            if (prev.field !== field) {
                return { field, direction: 'asc' };
            }

            if (prev.direction === 'asc') {
                return { field, direction: 'desc' };
            }

            return { field: null, direction: 'asc' };
        });
    };

    const hasMultiQty = useMemo(() => {
        const allOpts = [...(data?.options ?? []), ...(data?.allOptions ?? [])];
        return allOpts.some(o => o.qty > 1);
    }, [data?.options, data?.allOptions]);

    const availablePartTypes = useMemo(() => {
        const sourceOptions = viewMode === 'all' ? data?.allOptions : data?.options;
        if (!sourceOptions) return [];
        return getAvailablePartTypesFromOptions(sourceOptions);
    }, [data?.options, data?.allOptions, viewMode]);

    const hasFilterableOptions = useMemo(() => {
        const sourceOptions = viewMode === 'all' ? data?.allOptions : data?.options;
        return (sourceOptions?.length ?? 0) > 0;
    }, [data?.options, data?.allOptions, viewMode]);

    const sortedOptions = useMemo(() => {
        const sourceOptions = viewMode === 'all' ? data?.allOptions : data?.options;
        if (!sourceOptions) {
            return [];
        }

        const advancedFilteredOptions = applyPartOptionFilters(sourceOptions, partOptionFilters);

        const filteredOptions = searchQuery.trim()
            ? advancedFilteredOptions.filter((option) => optionMatchesSearch(option, searchQuery.toLowerCase().trim()))
            : advancedFilteredOptions;

        if (!sortState.field) {
            return filteredOptions;
        }

        const optionsToSort = [...filteredOptions];

        optionsToSort.sort((a, b) => {
            let aValue: number | string | null = null;
            let bValue: number | string | null = null;

            switch (sortState.field) {
                case 'delivery':
                    // Sort by deliveryString as string
                    aValue = a.deliveryString ?? '';
                    bValue = b.deliveryString ?? '';
                    break;
                case 'listPrice':
                    aValue = a.listPrice ?? null;
                    bValue = b.listPrice ?? null;
                    break;
                case 'buyPrice':
                    aValue = a.buyPrice ?? null;
                    bValue = b.buyPrice ?? null;
                    break;
                case 'grossMargin':
                    const aGrossMarginBasePrice = useListPriceForMargin ? a.listPrice : (a.adjustedListPrice ?? a.listPrice);
                    const bGrossMarginBasePrice = useListPriceForMargin ? b.listPrice : (b.adjustedListPrice ?? b.listPrice);
                    aValue = calculateGrossMargin(aGrossMarginBasePrice, a.buyPrice);
                    bValue = calculateGrossMargin(bGrossMarginBasePrice, b.buyPrice);
                    break;
            }

            if (aValue === null || aValue === undefined) {
                return bValue === null || bValue === undefined ? 0 : (sortState.direction === 'asc' ? 1 : -1);
            }

            if (bValue === null || bValue === undefined) {
                return sortState.direction === 'asc' ? -1 : 1;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                if (aValue === bValue) return 0;
                return sortState.direction === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            if (aValue === bValue) return 0;
            return sortState.direction === 'asc'
                ? (aValue as number) - (bValue as number)
                : (bValue as number) - (aValue as number);
        });

        return optionsToSort;
    }, [data?.options, data?.allOptions, sortState, useListPriceForMargin, viewMode, partOptionFilters, searchQuery]);

    const filteredNoQuotes = useMemo(() => {
        if (!data?.noQuotes) return [];
        if (!searchQuery.trim()) return data.noQuotes;
        const q = searchQuery.toLowerCase().trim();
        return data.noQuotes.filter((noQuote) => noQuoteMatchesSearch(noQuote, q));
    }, [data?.noQuotes, searchQuery]);

    const renderSortableHeader = (label: string, field: 'delivery' | 'listPrice' | 'buyPrice' | 'grossMargin', isNumeric: boolean = false) => {
        const isActive = sortState.field === field;
        const direction: SortIconDirection = isActive ? sortState.direction : 'none';

        return (
            <Flex
                as="button"
                type="button"
                align="center"
                justify={isNumeric ? "flex-end" : "flex-start"}
                gap="6px"
                w="100%"
                color={isActive ? "#005BC4" : "gray.600"}
                fontSize="xs"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="0.05em"
                cursor="pointer"
                onClick={(event) => {
                    event.stopPropagation();
                    toggleSort(field);
                }}
                _hover={{ color: "#005BC4" }}
                transition="color 0.2s"
            >
                <Text as="span">
                    {label}
                </Text>
                <Box lineHeight={0} color={isActive ? "#005BC4" : "gray.400"}>
                    <SortArrowIcon direction={direction} size={12} />
                </Box>
            </Flex>
        );
    };

    const handleCopyPartNumber = async (partNumber: string, key: string, e?: React.MouseEvent) => {
        if (!partNumber) return;
        const ok = await copyText(partNumber, key, e ? { x: e.clientX, y: e.clientY } : null);
        if (!ok) {
            setFailedCopyKey(key);
            if (failedCopyTimerRef.current !== null) {
                window.clearTimeout(failedCopyTimerRef.current);
            }
            failedCopyTimerRef.current = window.setTimeout(() => {
                setFailedCopyKey(null);
                failedCopyTimerRef.current = null;
            }, 4000);
            return;
        }

        if (failedCopyKey === key) {
            setFailedCopyKey(null);
        }
    };

    const getCopyTooltipProps = (key: string) => {
        const isFailure = failedCopyKey === key;
        return {
            isCopied: isFailure || isCopied(key),
            copiedLabel: isFailure ? 'Failed to copy' : undefined,
            bg: isFailure ? 'red.500' : undefined,
        };
    };

    return (
        <Modal isOpen={isOpen} onClose={closeModal} size="2xl" isCentered>
            <ModalOverlay />
            <ModalContent maxW="90vw" h="85vh" borderRadius="xl" overflow="visible">
                <Flex h="100%" overflow="visible">
                    {/* Left side - main content */}
                    <Flex flex="1" direction="column" overflow="visible" position="relative" minW={0}>
                        {/* Header — overflow visible so CopyTooltip can extend above without clipping */}
                        <Box
                            p={4}
                            bg="white"
                            borderBottom="1px solid"
                            borderColor="border.subtle"
                            borderTopLeftRadius="xl"
                            borderTopRightRadius={notePanelOption ? 0 : 'xl'}
                            overflow="visible"
                            position="relative"
                            zIndex={FILTER_MENU_Z_INDEX}
                        >
                            {data ? (
                                <VStack align="stretch" spacing={2}>
                                    {/* Line 1: Title + OE # + tags ... Options/No Quotes buttons on the right */}
                                    <Flex justify="space-between" align="center" wrap="nowrap">
                                        <HStack spacing={2} align="center" flex="1" minW={0}>
                                            <Text fontSize="lg" fontWeight="bold" color="gray.800" whiteSpace="nowrap">
                                                {data.title}
                                            </Text>
                                            <Text color="gray.300" fontSize="sm" fontWeight="light">|</Text>
                                            <HStack spacing={1} align="center" whiteSpace="nowrap">
                                                <Text fontSize="sm" color="gray.500">OE #:</Text>
                                                <CopyTooltip {...getCopyTooltipProps('oe')} zIndex={10}>
                                                    <Text as="span" fontWeight="bold" color="gray.800">{data.partNumber}</Text>
                                                </CopyTooltip>
                                                <IconButton
                                                    aria-label="Copy part number"
                                                    icon={<Icon as={TbCopy as React.ElementType} />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="gray.400"
                                                    _hover={{ color: "gray.600" }}
                                                    onClick={(e) => handleCopyPartNumber(data.partNumber, 'oe', e)}
                                                />
                                            </HStack>
                                            {data.correctedPartNumber && (
                                                <HStack spacing={1} align="center" whiteSpace="nowrap">
                                                    <Icon as={TbArrowRight as React.ElementType} color="gray.500" />
                                                    <CopyTooltip {...getCopyTooltipProps('corrected')} zIndex={10}>
                                                        <Text as="span" fontSize="sm" color="orange.500" fontWeight="bold">
                                                            {data.correctedPartNumber}
                                                        </Text>
                                                    </CopyTooltip>
                                                    <IconButton
                                                        aria-label="Copy corrected part number"
                                                        icon={<Icon as={TbCopy as React.ElementType} />}
                                                        size="xs"
                                                        variant="ghost"
                                                        color="gray.400"
                                                        _hover={{ color: "gray.600" }}
                                                        onClick={(e) => handleCopyPartNumber(data.correctedPartNumber!, 'corrected', e)}
                                                    />
                                                </HStack>
                                            )}
                                            {data.preOrder !== undefined && (
                                                <QuotingTag
                                                    tag={data.preOrder ? 'pre-order' : 'on-site'}
                                                    text={data.preOrder ? 'PRE-ORDER' : 'ON-SITE'}
                                                />
                                            )}
                                            {data.oemEndorsement && (
                                                <OrderTag
                                                    tag='oem-endorsement'
                                                    text='OEM ENDORSEMENT'
                                                />
                                            )}
                                            {data.oemCertification && (
                                                <OrderTag
                                                    tag='oem-cetification'
                                                    text='OEM CERTIFICATION'
                                                />
                                            )}
                                            {(!isEditable || !data.flag) && data.flagReason && (
                                                <Tooltip label={`Resolved flag — ${data.flagReason}`} placement="top" hasArrow>
                                                    <Box display="inline-flex">
                                                        <Icon as={TbFlag as React.ElementType} w={4} h={4} color="orange.400" />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </HStack>
                                        <HStack spacing={2} align="center" flexShrink={0} ml={4}>
                                            {!notePanelOption && (
                                                <SegmentedControl<TabOptions>
                                                    size="sm"
                                                    value={viewMode}
                                                    onChange={(next) => {
                                                        setViewMode(next);
                                                        setPartOptionFilters({});
                                                    }}
                                                    items={[
                                                        {
                                                            value: 'all',
                                                            label: 'All',
                                                            isDisabled: !data.allOptions || data.allOptions.length <= data.options.length,
                                                        },
                                                        { value: 'options', label: 'Options' },
                                                        {
                                                            value: 'noQuotes',
                                                            label: `No Quotes (${data.noQuotes?.length ?? 0})`,
                                                        },
                                                    ]}
                                                />
                                            )}
                                            {!notePanelOption && (
                                                <CloseButton
                                                    size="sm"
                                                    onClick={closeModal}
                                                    color="gray.500"
                                                    _hover={{ color: 'gray.700', bg: 'gray.100' }}
                                                />
                                            )}
                                        </HStack>
                                    </Flex>
                                    {/* Line 2: Estimate List Price | Insurance | RO # + part type filter */}
                                    <Flex justify="space-between" align="center">
                                        <HStack spacing={3} align="center" fontSize="sm" color="gray.600">
                                            {data.emsListPrice && (
                                                <HStack spacing={1} align="center">
                                                    <Text>
                                                        Estimate List Price
                                                    </Text>
                                                    <Tooltip
                                                        label={`This reflects the original price${data.estimatePartType ? ` for the ${getPartTypeCategory(data.estimatePartType)} part type` : ''} in the estimate from ${data.estimator === 'ccc' ? 'CCC' : 'Mitchell'}.`}
                                                        placement="right"
                                                        hasArrow
                                                    >
                                                        <Box display="inline-flex" alignItems="center">
                                                            <Icon
                                                                as={TbInfoCircle as React.ElementType}
                                                                w={3}
                                                                h={3}
                                                                color="gray.500"
                                                                cursor="pointer"
                                                                _hover={{ color: "gray.700" }}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                    <Text fontWeight="bold" color="gray.800">
                                                        : {formatPrice(data.emsListPrice)}
                                                    </Text>
                                                    {data.estimatePartType && (
                                                        <PartType partType={getPartTypeCategory(data.estimatePartType)} />
                                                    )}
                                                </HStack>
                                            )}
                                            {data.allowableListPrice && (
                                                <>
                                                    {data.emsListPrice && <Text color="gray.300" fontWeight="light">|</Text>}
                                                    <HStack spacing={1} align="center">
                                                        <Text>
                                                            Allowable List Price
                                                        </Text>
                                                        <Tooltip
                                                            label="This is the price that is the maximum allowed by the insurance company."
                                                            placement="right"
                                                            hasArrow
                                                        >
                                                            <Box display="inline-flex" alignItems="center">
                                                                <Icon
                                                                    as={TbInfoCircle as React.ElementType}
                                                                    w={3}
                                                                    h={3}
                                                                    color="gray.500"
                                                                    cursor="pointer"
                                                                    _hover={{ color: "gray.700" }}
                                                                />
                                                            </Box>
                                                        </Tooltip>
                                                        <Text fontWeight="bold" color="gray.800">
                                                            : {formatPrice(data.allowableListPrice)}
                                                        </Text>
                                                    </HStack>
                                                </>
                                            )}
                                            {data.insurance && (
                                                <>
                                                    {(data.emsListPrice || data.allowableListPrice) && <Text color="gray.300" fontWeight="light">|</Text>}
                                                    <Text>
                                                        Insurance: <Text as="span" fontWeight="bold" color="gray.800">{data.insurance}</Text>
                                                    </Text>
                                                </>
                                            )}
                                            <Text color="gray.300" fontWeight="light">|</Text>
                                            <Text>
                                                RO #: <Text as="span" fontWeight="bold" color="gray.800">{data.roNumber}</Text>
                                            </Text>
                                            <Text color="gray.300" fontWeight="light">|</Text>
                                            <Text>
                                                Mileage: <Text as="span" fontWeight="bold" color="gray.800">{data.mileage?.toLocaleString() ?? '—'}</Text>
                                            </Text>
                                        </HStack>
                                        <HStack spacing={3} flexShrink={0} ml={4}>
                                            {!notePanelOption && viewMode !== 'noQuotes' && hasFilterableOptions && (
                                                <PartOptionAdvancedFilters
                                                    filters={partOptionFilters}
                                                    onFiltersChange={setPartOptionFilters}
                                                    availablePartTypes={availablePartTypes}
                                                />
                                            )}
                                            {!notePanelOption && (
                                                <InputGroup size="sm" w="350px">
                                                    <InputLeftElement pointerEvents="none">
                                                        <Icon as={TbSearch as React.ElementType} color="gray.400" boxSize={3.5} />
                                                    </InputLeftElement>
                                                    <Input
                                                        placeholder="Search..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        borderRadius="md"
                                                        borderColor="gray.300"
                                                        _hover={{ borderColor: 'gray.400' }}
                                                        _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
                                                        fontSize="sm"
                                                    />
                                                </InputGroup>
                                            )}
                                        </HStack>
                                    </Flex>
                                </VStack>
                            ) : (
                                'Part Options'
                            )}
                        </Box>
                        {/* Body */}
                        <Box flex="1" overflow="hidden" display="flex" flexDirection="column" px={6} pt={4} bg="white">
                            {isLoading && (
                                <Flex justify="center" align="center" h="100%">
                                    <Spinner size="xl" color="blue.500" />
                                </Flex>
                            )}

                            {error && (
                                <Alert status="error">
                                    <AlertIcon />
                                    <AlertTitle>Error loading part options</AlertTitle>
                                    <AlertDescription>
                                        Please try again later or contact support if the problem persists.
                                    </AlertDescription>
                                </Alert>
                            )}

                            {data && (data.options.length > 0 || (viewMode === 'noQuotes' && data.noQuotes && data.noQuotes.length > 0)) && (
                                <VStack spacing={4} align="stretch" h="100%">
                                    {data.excludedForIncorrectPartNumber && data.excludeOrdering && (
                                        <Alert
                                            status="error"
                                            borderRadius="md"
                                            border="1px solid"
                                            borderColor="red.400"
                                            bg="red.50"
                                        >
                                            <AlertIcon color="red.500" />
                                            <AlertDescription color="gray.700" fontSize="sm">
                                                This part is excluded because it's the incorrect part number. Please do not re-include this part unless absolutely necessary. It's being re-quoted!
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    {data.flagReason && isEditable && data.flag && (
                                        <Alert
                                            status="warning"
                                            variant="subtle"
                                            bg="orange.50"
                                            border="1px solid"
                                            borderColor="orange.200"
                                            borderRadius="md"
                                            p={4}
                                            gap={3}
                                            alignItems="center"
                                        >
                                            <Icon
                                                as={TbFlag as React.ElementType}
                                                w={5}
                                                h={5}
                                                color="orange.400"
                                            />
                                            <AlertDescription fontSize="sm" color="orange.800">
                                                {data.flagReason}
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    {viewMode === 'noQuotes' ? (
                                        <TableContainer
                                            ref={noQuoteScrollRef}
                                            {...noQuoteScrollProps}
                                            border="1px solid"
                                            borderColor="gray.200"
                                            borderRadius="lg"
                                            flex="1"
                                            minH="0"
                                            overflowY="auto"
                                            sx={stickyScrollTableHeadSx}
                                        >
                                            <Table variant="simple" size="sm" sx={noQuoteCopyTableSx}>
                                                <Thead position="sticky" top={0} bg="bg.subtle" zIndex={STICKY_TABLE_HEADER_Z_INDEX}>
                                                    <Tr>
                                                        <Th py={3}>
                                                            <Text color="gray.600" fontSize="xs" fontWeight="semibold">
                                                                VENDOR NAME
                                                            </Text>
                                                        </Th>
                                                        <Th py={3}>
                                                            <Text color="gray.600" fontSize="xs" fontWeight="semibold">
                                                                NO QUOTE #
                                                            </Text>
                                                        </Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {filteredNoQuotes.length === 0 ? (
                                                        <Tr>
                                                            <Td colSpan={2} py={12} textAlign="center">
                                                                <Text fontSize="sm" color="gray.500">
                                                                    {searchQuery.trim() ? 'No matching vendors found' : 'No vendors without quotes'}
                                                                </Text>
                                                            </Td>
                                                        </Tr>
                                                    ) : filteredNoQuotes.map((noQuote, nqIndex) => (
                                                        <Tr key={noQuote._id} borderBottom="1px solid" borderColor="gray.100">
                                                            <Td py={3}>
                                                                <Text color="gray.900" fontSize="sm">
                                                                    {noQuote.supplierText}
                                                                </Text>
                                                            </Td>
                                                            <Td py={3}>
                                                                <HStack spacing={2}>
                                                                    <CopyTooltip
                                                                        {...getCopyTooltipProps(`no-quote-${noQuote._id}`)}
                                                                        zIndex={10}
                                                                        placement={nqIndex === 0 ? 'bottom' : 'top'}
                                                                    >
                                                                        <Text as="span" color="gray.600" fontSize="sm">
                                                                            {noQuote.noQuoteNumber}
                                                                        </Text>
                                                                    </CopyTooltip>
                                                                    <IconButton
                                                                        aria-label="Copy no quote number"
                                                                        icon={<Icon as={TbCopy as React.ElementType} />}
                                                                        size="xs"
                                                                        variant="ghost"
                                                                        color="gray.400"
                                                                        _hover={{ color: "gray.600" }}
                                                                        onClick={(e) => handleCopyPartNumber(noQuote.noQuoteNumber, `no-quote-${noQuote._id}`, e)}
                                                                    />
                                                                </HStack>
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <TableContainer
                                            ref={optionsScrollRef}
                                            {...optionsScrollProps}
                                            border="1px solid"
                                            borderColor="gray.200"
                                            borderRadius="lg"
                                            flex="1"
                                            minH="0"
                                            overflowY="auto"
                                            sx={stickyTableScrollCopySx}
                                        >
                                            <Table variant="simple" size="sm" sx={copyOverflowTableSx}>
                                                <Thead position="sticky" top={0} bg="bg.subtle" zIndex={STICKY_TABLE_HEADER_Z_INDEX}>
                                                    <Tr>
                                                        <Th py={3} >
                                                            <Text color="gray.600" fontSize="xs" fontWeight="semibold">
                                                                VENDOR NAME
                                                            </Text>
                                                        </Th>
                                                        <Th py={3} whiteSpace="nowrap">
                                                            <Text color="gray.600" fontSize="xs" fontWeight="semibold">
                                                                SOURCE
                                                            </Text>
                                                        </Th>
                                                        <Th py={3} whiteSpace="nowrap">
                                                            <Text color="gray.600" fontSize="xs" fontWeight="semibold">
                                                                VALID
                                                            </Text>
                                                        </Th>
                                                        <Th py={3} whiteSpace="nowrap">
                                                            {renderSortableHeader('DELIVERY', 'delivery')}
                                                        </Th>
                                                        <Th py={3} whiteSpace="nowrap" isNumeric>
                                                            <Flex align="center" justify="flex-end" gap={2}>
                                                                <Text color="gray.600" fontSize="xs" fontWeight="semibold">
                                                                    QTY
                                                                </Text>
                                                                {hasMultiQty && (
                                                                    <Tooltip
                                                                        label="Show per unit pricing"
                                                                        placement="top"
                                                                        hasArrow
                                                                    >
                                                                        <Box>
                                                                            <Switch
                                                                                size="sm"
                                                                                colorScheme="blue"
                                                                                isChecked={showPerUnit}
                                                                                onChange={(e) => setShowPerUnit(e.target.checked)}
                                                                            />
                                                                        </Box>
                                                                    </Tooltip>
                                                                )}
                                                            </Flex>
                                                        </Th>
                                                        <Th py={3} isNumeric>
                                                            {renderSortableHeader('LIST PRICE', 'listPrice', true)}
                                                        </Th>
                                                        <Th py={3} isNumeric>
                                                            <Flex align="center" justify="flex-end" gap={2}>
                                                                <Text color="gray.600" fontSize="xs" fontWeight="semibold">
                                                                    ADJ. LIST PRICE
                                                                </Text>
                                                                <Tooltip
                                                                    label="Remove Adj. List Price"
                                                                    placement="top"
                                                                    hasArrow
                                                                >
                                                                    <Box>
                                                                        <Switch
                                                                            size="sm"
                                                                            colorScheme="blue"
                                                                            isChecked={useListPriceForMargin}
                                                                            onChange={(e) => setUseListPriceForMargin(e.target.checked)}
                                                                        />
                                                                    </Box>
                                                                </Tooltip>
                                                            </Flex>
                                                        </Th>
                                                        <Th py={3} isNumeric>
                                                            {renderSortableHeader('BUY PRICE', 'buyPrice', true)}
                                                        </Th>
                                                        <Th py={3} isNumeric>
                                                            {renderSortableHeader('MARGIN', 'grossMargin', true)}
                                                        </Th>
                                                    </Tr>
                                                </Thead>
                                                {sortedOptions.length === 0 ? (
                                                    <Tbody>
                                                        <Tr>
                                                            <Td colSpan={9} py={12} textAlign="center">
                                                                <Text fontSize="sm" color="gray.500">
                                                                    {searchQuery.trim() ? 'No matching options found' : 'No part options available'}
                                                                </Text>
                                                            </Td>
                                                        </Tr>
                                                    </Tbody>
                                                ) : sortedOptions.map(option => {
                                                    const isSelected = selectedOption?._id === option._id;
                                                    const isOriginalSelection = option.originalSelected === true && !isSelected;
                                                    const divisor = showPerUnit ? option.qty : 1;
                                                    const displayListPrice = option.listPrice / divisor;
                                                    const displayBuyPrice = option.buyPrice / divisor;
                                                    const displayPreAdjustmentBuyPrice = option.preAdjustmentBuyPrice != null
                                                        ? option.preAdjustmentBuyPrice / divisor
                                                        : null;
                                                    const hasBuyPriceAdjustment = displayPreAdjustmentBuyPrice != null
                                                        && displayPreAdjustmentBuyPrice !== displayBuyPrice;
                                                    const displayAdjListPrice = option.adjustedListPrice ? option.adjustedListPrice / divisor : null;

                                                    const hasPreview = notePanelOption != null && previewListPrice != null;
                                                    const isEditTarget = hasPreview && notePanelOption?._id === option._id;
                                                    const isPreviewTarget = hasPreview && (isEditTarget || option.adjustedListPrice != null);
                                                    const previewAdjPrice = isPreviewTarget ? previewListPrice! / divisor : null;

                                                    const effectiveAdjPrice = previewAdjPrice ?? displayAdjListPrice;
                                                    const grossMarginBasePrice = useListPriceForMargin ? displayListPrice : (effectiveAdjPrice ?? displayListPrice);
                                                    const adjustedListPriceDisplay = useListPriceForMargin ? '—' : (effectiveAdjPrice ? formatPrice(effectiveAdjPrice) : '—');

                                                    const priorAdjPrice = displayAdjListPrice ?? displayListPrice;
                                                    const priorMargin = useListPriceForMargin ? null : (priorAdjPrice !== 0 ? calculateGrossMargin(priorAdjPrice, displayBuyPrice) : 0);
                                                    const currentMargin = calculateGrossMargin(grossMarginBasePrice, displayBuyPrice);
                                                    const preAdjustmentMargin = hasBuyPriceAdjustment
                                                        ? calculateGrossMargin(grossMarginBasePrice, displayPreAdjustmentBuyPrice)
                                                        : null;
                                                    const marginDelta = isPreviewTarget && priorMargin != null ? currentMargin - priorMargin : null;
                                                    const priceDelta = isPreviewTarget ? (previewAdjPrice! - priorAdjPrice) : null;
                                                    const isValid = isPartOptionValid(option);
                                                    const invalidReason = getPartOptionInvalidReason(option);
                                                    return (
                                                        <Tbody
                                                            key={option._id}
                                                            bg={isSelected ? "blue.50" : isOriginalSelection ? "gray.100" : "white"}
                                                            borderLeft={isSelected || isOriginalSelection ? "4px solid" : "none"}
                                                            borderLeftColor={isSelected ? "blue.500" : isOriginalSelection ? "gray.500" : "transparent"}
                                                            opacity={option.selectable ? 1 : 0.6}
                                                            _hover={!option.selectable ? {} : { bg: isSelected ? "blue.100" : isOriginalSelection ? "gray.200" : "gray.100", cursor: "pointer" }}
                                                            onClick={!option.selectable ? undefined : () => handleOptionSelect(option)}
                                                            borderBottom="1px solid #E2E8F0"
                                                            sx={{
                                                                transition: "background-color 0.2s",
                                                                cursor: !option.selectable ? "not-allowed" : "pointer",
                                                            }}
                                                        >
                                                            <Tr>
                                                                <Td py={4} borderBottom="none">
                                                                    <HStack spacing={2} align="center">
                                                                        {/* Stack icons vertically if both are present */}
                                                                        {(option.insurerBest || option.bestPrice || option.partbayInsuranceBest) && (
                                                                            <VStack spacing={0.5} align="center">
                                                                                {option.insurerBest && (
                                                                                    <Box
                                                                                        w="12px"
                                                                                        h="12px"
                                                                                        bg="green.500"
                                                                                        borderRadius="4px"
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                        justifyContent="center"
                                                                                        title="Insurer's best choice"
                                                                                    >
                                                                                        <Text fontSize="8px" color="white" fontWeight="bold" lineHeight="1">i</Text>
                                                                                    </Box>
                                                                                )}
                                                                                {option.bestPrice && (
                                                                                    <Box
                                                                                        w="12px"
                                                                                        h="12px"
                                                                                        bg="gray.800"
                                                                                        borderRadius="4px"
                                                                                        display="flex"
                                                                                        alignItems="center"
                                                                                        justifyContent="center"
                                                                                        title="Best price"
                                                                                    >
                                                                                        <Text fontSize="8px" color="white" fontWeight="bold" lineHeight="1">$</Text>
                                                                                    </Box>
                                                                                )}
                                                                                {option.partbayInsuranceBest && (
                                                                                    <Tooltip label={brandCopy('PartBay Insurer Best')} placement="top" hasArrow>
                                                                                        <Box
                                                                                            w="12px"
                                                                                            h="12px"
                                                                                            borderRadius="4px"
                                                                                            display="flex"
                                                                                            alignItems="center"
                                                                                            justifyContent="center"
                                                                                        >
                                                                                            <Image src={YaxleLogo} w="12px" h="12px" objectFit="contain" alt="" />
                                                                                        </Box>
                                                                                    </Tooltip>
                                                                                )}
                                                                            </VStack>
                                                                        )}
                                                                        {option.vendorType && (
                                                                            <VendorTypeIcon type={option.vendorType} />
                                                                        )}
                                                                        <Text color="gray.900" fontSize="sm" fontWeight={isSelected ? "semibold" : "normal"} whiteSpace="nowrap">
                                                                            {option.supplierText}
                                                                        </Text>
                                                                        <PartType
                                                                            partType={option.type}
                                                                        />
                                                                        <Popover trigger="hover" placement="top" openDelay={100} closeDelay={150} isLazy>
                                                                            <PopoverTrigger>
                                                                                <IconButton
                                                                                    aria-label="Supplier information"
                                                                                    icon={<Icon as={TbInfoCircle as React.ElementType} />}
                                                                                    size="xs"
                                                                                    variant="ghost"
                                                                                    color="gray.500"
                                                                                    _hover={{ color: "gray.700" }}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                            </PopoverTrigger>
                                                                            <Portal>
                                                                                <PopoverContent
                                                                                    bg="gray.700"
                                                                                    color="white"
                                                                                    border="none"
                                                                                    borderRadius="md"
                                                                                    w="auto"
                                                                                    maxW="320px"
                                                                                    zIndex={1500}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                    _focus={{ boxShadow: 'none', outline: 'none' }}
                                                                                >
                                                                                    <PopoverArrow bg="gray.700" />
                                                                                    <PopoverBody fontSize="sm" px={3} py={2}>
                                                                                        <VStack align="stretch" spacing={1.5}>
                                                                                            <HStack spacing={1.5} align="center">
                                                                                                <Text>Part #:</Text>
                                                                                                <CopyTooltip {...getCopyTooltipProps(`supplier-pn-${option._id}`)} zIndex={10}>
                                                                                                    <Text as="span" fontWeight="semibold">
                                                                                                        {option.supplierPartNumber}
                                                                                                    </Text>
                                                                                                </CopyTooltip>
                                                                                                <IconButton
                                                                                                    aria-label="Copy supplier part number"
                                                                                                    icon={<Icon as={TbCopy as React.ElementType} boxSize={3.5} />}
                                                                                                    size="xs"
                                                                                                    variant="ghost"
                                                                                                    color="white"
                                                                                                    _hover={{ bg: 'whiteAlpha.300' }}
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleCopyPartNumber(option.supplierPartNumber, `supplier-pn-${option._id}`, e);
                                                                                                    }}
                                                                                                />
                                                                                            </HStack>
                                                                                            {option.supplierNote && (
                                                                                                <Text whiteSpace="pre-wrap">
                                                                                                    {option.supplierNote}
                                                                                                </Text>)}
                                                                                        </VStack>
                                                                                    </PopoverBody>
                                                                                </PopoverContent>
                                                                            </Portal>
                                                                        </Popover>
                                                                    </HStack>
                                                                </Td>
                                                                <Td py={4} borderBottom="none">
                                                                    <SourceBadge source={option.source} />
                                                                </Td>
                                                                <Td py={4} borderBottom="none">
                                                                    <HStack spacing={1} align="center">
                                                                        <Text fontSize="sm" color="gray.900">
                                                                            {isValid ? 'Yes' : 'No'}
                                                                        </Text>
                                                                        {!isValid && invalidReason && (
                                                                            <Tooltip label={invalidReason} placement="top" hasArrow>
                                                                                <Box display="inline-flex" alignItems="center">
                                                                                    <Icon
                                                                                        as={TbInfoCircle as React.ElementType}
                                                                                        w={3.5}
                                                                                        h={3.5}
                                                                                        color="gray.500"
                                                                                        cursor="pointer"
                                                                                    />
                                                                                </Box>
                                                                            </Tooltip>
                                                                        )}
                                                                    </HStack>
                                                                </Td>
                                                                <Td py={4} borderBottom="none">
                                                                    <Text color="gray.900" fontSize="sm">
                                                                        {option.deliveryString}
                                                                    </Text>
                                                                </Td>
                                                                <Td py={4} isNumeric borderBottom="none">
                                                                    <Text color="gray.900" fontSize="sm">
                                                                        {option.qty}
                                                                    </Text>
                                                                </Td>
                                                                <Td py={4} isNumeric borderBottom="none">
                                                                    <Text color="gray.900" fontSize="sm" fontWeight="medium">
                                                                        {formatPrice(displayListPrice)}
                                                                    </Text>
                                                                </Td>
                                                                <Td py={4} isNumeric borderBottom="none">
                                                                    <HStack spacing={1} justify="flex-end">
                                                                        {(isEditable || data.adjustListPricePostPlacement) && !useListPriceForMargin && isSelected && (
                                                                            <IconButton
                                                                                aria-label="Edit note"
                                                                                icon={<Icon as={TbPencil as React.ElementType} w={3.5} h={3.5} />}
                                                                                size="xs"
                                                                                variant="ghost"
                                                                                color="gray.400"
                                                                                _hover={{ color: "blue.500" }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openNotePanel(option);
                                                                                }}
                                                                            />
                                                                        )}
                                                                        <Flex align="center" justify="flex-end" gap={1.5}>
                                                                            <Text color={isPreviewTarget ? '#0071E3' : 'gray.900'} fontSize="sm" fontWeight="medium">
                                                                                {adjustedListPriceDisplay}
                                                                            </Text>
                                                                            {priceDelta != null && priceDelta !== 0 && (
                                                                                <Flex align="center" gap={0.5} fontStyle="italic">
                                                                                    <Icon as={(priceDelta >= 0 ? TbArrowUp : TbArrowDown) as React.ElementType} color={priceDelta <= 0 ? 'green.500' : 'red.500'} boxSize={3} />
                                                                                    <Text fontSize="xs" fontWeight="medium" color={priceDelta <= 0 ? 'green.500' : 'red.500'}>
                                                                                        {formatPrice(Math.abs(priceDelta))}
                                                                                    </Text>
                                                                                </Flex>
                                                                            )}
                                                                        </Flex>
                                                                    </HStack>
                                                                </Td>
                                                                <Td py={4} isNumeric borderBottom="none">
                                                                    <Flex align="center" justify="flex-end" h="100%">
                                                                        {hasBuyPriceAdjustment ? (
                                                                            <VStack align="flex-end" spacing={0}>
                                                                                <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                                                    {formatPrice(displayPreAdjustmentBuyPrice)}
                                                                                </Text>
                                                                                <Text fontSize="sm" fontWeight="medium" color="orange.500">
                                                                                    {formatPrice(displayBuyPrice)}
                                                                                </Text>
                                                                            </VStack>
                                                                        ) : (
                                                                            <Text color="gray.900" fontSize="sm" fontWeight="medium">
                                                                                {formatPrice(displayBuyPrice)}
                                                                            </Text>
                                                                        )}
                                                                    </Flex>
                                                                </Td>
                                                                <Td py={4} isNumeric borderBottom="none">
                                                                    <Flex align="center" justify="flex-end" gap={1.5}>
                                                                        {hasBuyPriceAdjustment && preAdjustmentMargin != null ? (
                                                                            <VStack align="flex-end" spacing={0}>
                                                                                <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                                                    {preAdjustmentMargin.toFixed(1)}%
                                                                                </Text>
                                                                                <Text fontSize="sm" color={isPreviewTarget ? '#0071E3' : 'orange.500'}>
                                                                                    {currentMargin.toFixed(1)}%
                                                                                </Text>
                                                                            </VStack>
                                                                        ) : (
                                                                            <Text color={isPreviewTarget ? '#0071E3' : 'gray.900'} fontSize="sm">
                                                                                {currentMargin.toFixed(1)}%
                                                                            </Text>
                                                                        )}
                                                                        {marginDelta != null && marginDelta !== 0 && (
                                                                            <Flex align="center" gap={0.5} fontStyle="italic">
                                                                                <Icon as={(marginDelta >= 0 ? TbArrowUp : TbArrowDown) as React.ElementType} color={marginDelta >= 0 ? 'green.500' : 'red.500'} boxSize={3} />
                                                                                <Text fontSize="xs" fontWeight="medium" color={marginDelta >= 0 ? 'green.500' : 'red.500'}>
                                                                                    {Math.abs(marginDelta).toFixed(1)}%
                                                                                </Text>
                                                                            </Flex>
                                                                        )}
                                                                    </Flex>
                                                                </Td>
                                                            </Tr>
                                                            {option.supplierComment && (
                                                                <Tr>
                                                                    <Td colSpan={9} pt={0} pb={4} whiteSpace="normal" borderBottom="none">
                                                                        <Text fontSize="sm" color="gray.400" fontStyle="italic">
                                                                            {option.supplierComment}
                                                                        </Text>
                                                                    </Td>
                                                                </Tr>
                                                            )}
                                                            {isSelected && option.optionNote && (
                                                                <Tr>
                                                                    <Td colSpan={9} pt={0} pb={3} borderBottom="none">
                                                                        <HStack
                                                                            spacing={2}
                                                                            align="center"
                                                                            border="1px solid"
                                                                            borderColor="blue.200"
                                                                            borderRadius="md"
                                                                            px={3}
                                                                            py={2}
                                                                        >
                                                                            <Icon
                                                                                as={TbMessage as React.ElementType}
                                                                                w={4}
                                                                                h={4}
                                                                                color="blue.400"
                                                                                flexShrink={0}
                                                                            />
                                                                            <Text fontSize="sm" color="gray.600">
                                                                                {option.optionNote}
                                                                            </Text>
                                                                        </HStack>
                                                                    </Td>
                                                                </Tr>
                                                            )}
                                                        </Tbody>
                                                    );
                                                })}
                                            </Table>
                                        </TableContainer>
                                    )}
                                </VStack>
                            )}

                            {data && data.options.length === 0 && (
                                <Center py={8}>
                                    <Text color="gray.500">No part options available</Text>
                                </Center>
                            )}
                        </Box>

                        {/* Footer */}
                        <Box px={6} py={4}>
                            <Flex justifyContent="space-between" alignItems="center" w="100%">
                                <HStack spacing={3} alignItems='center'>
                                    <HStack spacing={1} alignItems='center'>
                                        <Text fontSize="xs" color="gray.600">
                                            Part data powered by
                                        </Text>
                                        <MotorIcon style={{ width: '36px', height: '36px' }} />
                                    </HStack>
                                    {data?.isFord && (
                                        <>
                                            <Text color="gray.400">•</Text>
                                            <Text fontSize="xs" color="gray.600">
                                                Find Ford OEM parts here: <Link href="https://www.fordparts.com" isExternal color="blue.500" fontWeight="medium">fordparts.com</Link>
                                            </Text>
                                        </>
                                    )}
                                </HStack>
                                {isEditable && !notePanelOption ? <HStack spacing={3}>
                                    <Button variant="outlineGray" onClick={closeModal}>
                                        Cancel
                                    </Button>
                                    <Tooltip
                                        label="A PartsTrader note is required before saving this recycled option."
                                        isDisabled={!selectedOption || !optionRequiresNote(selectedOption) || !!selectedOption.optionNote?.trim()}
                                        shouldWrapChildren
                                    >
                                        <Button
                                            bg="#0071E3"
                                            color="white"
                                            _hover={{ bg: "#005BC4" }}
                                            onClick={handleSaveChanges}
                                            isDisabled={isLoading || !isSaveAllowed || (!!selectedOption && optionRequiresNote(selectedOption) && !selectedOption.optionNote?.trim())}
                                            isLoading={isUpdating}
                                            loadingText="Saving..."
                                        >
                                            {isConfirmingSelection ? 'Confirm Selection' : 'Save Changes'}
                                        </Button>
                                    </Tooltip>
                                </HStack> : null}
                            </Flex>
                        </Box>
                    </Flex>

                    {/* Right side - full-height note panel */}
                    {notePanelOption && (
                        <Flex
                            w="380px"
                            flexShrink={0}
                            direction="column"
                            borderLeft="1px solid"
                            borderColor="border.subtle"
                            bg="white"
                            borderTopRightRadius="xl"
                            borderBottomRightRadius="xl"
                        >
                            {/* Note panel header */}
                            <Flex
                                justify="space-between"
                                align="center"
                                px={5}
                                py={5}
                                borderBottom="1px solid"
                                borderColor="gray.200"
                                flexShrink={0}
                                borderTopRightRadius="xl"
                            >
                                <Text fontSize="md" fontWeight="semibold" color="gray.800">
                                    {isEditable
                                        ? 'Adjust List Price / Add Insurer Note'
                                        : data?.adjustListPricePostPlacement
                                            ? 'Adjust List Price'
                                            : 'Insurer Note'}
                                </Text>
                                <CloseButton size="sm" onClick={closeNotePanel} />
                            </Flex>

                            {/* Note panel body */}
                            <Box flex="1" overflow="auto" px={5} py={6}>
                                <PartOptionNoteForm
                                    key={notePanelOption._id}
                                    selectedOptionId={notePanelOption._id}
                                    orderId={orderId}
                                    orderPartId={orderPartId}
                                    notes={notePanelOption.optionNote ?? ''}
                                    estimatingNote={data?.estimateNote ?? ''}
                                    listPriceAdjusted={notePanelOption.listPriceAdjusted}
                                    isEditable={isEditable}
                                    canAdjustListPrice={isEditable || !!data?.adjustListPricePostPlacement}
                                    onClose={closeNotePanel}
                                    refetch={refetch}
                                    compact
                                    onModifiedListPriceChange={setPreviewListPrice}
                                    onSaved={(note) => {
                                        setSelectedOption(prev => (
                                            prev && prev._id === notePanelOption._id
                                                ? { ...prev, optionNote: note }
                                                : prev
                                        ));
                                    }}
                                    partType={notePanelOption.type}
                                    source={notePanelOption.source}
                                    insurerBest={notePanelOption.insurerBest}
                                    drpJob={drpJob}
                                />
                            </Box>
                        </Flex>
                    )}
                </Flex>
            </ModalContent>
            <AlertDialog
                isOpen={isCorrectedPartConfirmOpen}
                leastDestructiveRef={cancelCorrectedPartRef}
                onClose={() => setIsCorrectedPartConfirmOpen(false)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Confirm Part Change
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            This part was quoted based on an incorrect part number for this vehicle, so this part may not fit this vehicle. Are you sure you want to proceed?
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button
                                size="action"
                                ref={cancelCorrectedPartRef}
                                variant="outlineGray"
                                onClick={() => setIsCorrectedPartConfirmOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="action"
                                bg="#0071E3"
                                color="white"
                                _hover={{ bg: "#005BC4" }}
                                onClick={handleConfirmCorrectedPartSwitch}
                                isLoading={isUpdating}
                                loadingText="Saving..."
                                ml={3}
                            >
                                Proceed
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
            <ConfirmDialog
                isOpen={isInvalidOptionConfirmOpen}
                onClose={() => setIsInvalidOptionConfirmOpen(false)}
                onConfirm={handleConfirmInvalidOption}
                title="Potentially prohibited option"
                confirmLabel="Acknowledge"
                isConfirmLoading={isUpdating}
            >
                You are selecting an option that may be prohibited by {data?.insurance?.trim() || 'this carrier'}.
                Acknowledge that you are choosing a potentially invalid part for this order.
            </ConfirmDialog>
        </Modal>
    );
};

export default PartOptionsModal;